/**
 * Quick Start 트랙 — 입문 · 중급 · 고급. 각 트랙은 빈 워크플로에서 시작해 저장 → 배포 → 실행 → 결과 확인까지 실제로 돈다.
 *
 * **글은 언어별 문서**(content/ko.ts · en.ts)에, **동작은 여기**에 있다. 「대신 해줘」의 도구 호출은 매개변수까지
 * 고정이다(clientTools 와 같은 실행 경로, LLM 없음) — 어느 언어로 해도 같은 워크플로가 만들어져야 "따라 하기"가
 * 성립한다. 노드 설정은 검증된 샘플에서 그대로 가져왔다 — 입문: 샘플①(csv_to_db), 중급: 샘플③(분기/합류),
 * 고급: 샘플⑩(품질검사). 그래서 실행 결과도 샘플의 기대값과 같다(입문 7행, 중급 1행, 고급 6행 + 위반 파일 1행).
 *
 * 노드 **이름**만 언어를 따른다(캔버스에 저장되는 데이터). add_node 와 connect_nodes 가 같은 이름을 봐야 하므로
 * 이름은 content 의 `nodes` 한 곳에서 온다.
 *
 * 공통 후반부(저장·배포·실행·확인·마무리)는 tail() 로 만든다 — 확인 도구와 기대값만 트랙마다 다르다.
 */
import type { HelpAction } from './helpWorkflow';
import i18n from '../i18n';
import { typeLabelOf } from '../components/workflow/nodeCatalog';
import { helpContent, HelpContent } from './content';
import type { StepText, TrackText } from './content/types';

export interface QuickStep {
  title: string;
  text: string;
  options: string[];
  /** 단계에 들어설 때 자동으로 수행 */
  action?: HelpAction;
  /** 「대신 해줘」가 그대로 실행하는 도구 호출 */
  doActions?: HelpAction[];
  doneText?: string;
  /** 단계에 들어서면 손가락이 가리킬 곳 (help/pointer) — 없으면 가리키지 않는다 */
  point?: StepPoint;
}

export interface QuickTrack {
  id: 'basic' | 'inter' | 'adv';
  /** 메뉴 버튼 글 */
  label: string;
  /** 연습 워크플로 이름 */
  workflow: string;
  steps: QuickStep[];
}

const tool = (name: string, input: Record<string, unknown> = {}): HelpAction => ({ type: 'tool', name, input });

// 샘플에서 가져온 노드 설정 (backend/scripts/samples/*.json 과 같다)
const PG = 'openflow_test_pg';
const ORDER_COLS = ['order_id', 'customer', 'amount', 'order_dt', 'status'];
const MOVE = { transformMode: 'MOVE', mapping: [], mode: 'WRITE', useBulkLoader: false };

/** 단계 수를 넣어 제목을 만든다 */
const n = (i: number, total: number, title: string) => `${i} / ${total} · ${title}`;

/** 트랙 하나의 동작 — 단계 글(content.tracks[id].steps)과 **순서가 같아야 한다** */
interface TrackActions {
  /** 단계에 들어설 때 자동 실행 (1단계의 워크플로 만들기) */
  enter?: (t: TrackText) => HelpAction;
  /** 「대신 해줘」 */
  steps: Array<((t: TrackText) => HelpAction[]) | null>;
  /** 결과 확인 단계의 도구 */
  verify: HelpAction[];
}

const createWorkflow = (t: TrackText): HelpAction =>
  tool('create_workflow', { name: t.workflow, description: t.workflowDesc, folder: t.folder, reset: true });

const connect = (source: string, target: string, sourceHandle?: string): HelpAction =>
  tool('connect_nodes', { source, target, ...(sourceHandle ? { sourceHandle } : {}) });

/** 중급·고급이 함께 쓰는 앞부분: DB 소스 → 추출 → 내부데이터 */
const dbSourceChain = (w: Record<string, string>): HelpAction[] => [
  tool('add_node', { type: 'rdbData', label: w.srcOrders, position: { x: 40, y: 120 }, config: { connection: PG, table: 'of_test_src_orders', mode: 'READ', writeMode: 'INSERT', columns: ORDER_COLS } }),
  tool('add_node', { type: 'transformLoad', label: w.extract, position: { x: 300, y: 120 }, config: MOVE }),
  tool('add_node', { type: 'innerData', label: w.orderData, position: { x: 560, y: 120 }, config: {} }),
  connect(w.srcOrders, w.extract),
  connect(w.extract, w.orderData),
];

const ACTIONS: Record<QuickTrack['id'], TrackActions> = {
  basic: {
    enter: createWorkflow,
    steps: [
      null,                                     // 1 새 워크플로 (enter 가 한다)
      (t) => [tool('add_node', { type: 'csvData', label: t.nodes.orderFile, config: { filepath: 'orders.csv', delimiter: ',', mode: 'READ', hasHeader: true, encoding: 'UTF-8' } })],
      (t) => [
        tool('add_node', { type: 'transformLoad', label: t.nodes.process, config: MOVE }),
        connect(t.nodes.orderFile, t.nodes.process),
      ],
      (t) => [
        tool('add_node', { type: 'rdbData', label: t.nodes.targetOrders, config: { connection: PG, table: 'of_test_tgt_orders_csv', mode: 'WRITE', writeMode: 'INSERT', columns: ORDER_COLS } }),
        connect(t.nodes.process, t.nodes.targetOrders),
      ],
      null,                                     // 5 매핑 (읽고 넘어가는 단계)
    ],
    verify: [tool('count_table_rows', { connection: PG, table: 'of_test_tgt_orders_csv' })],
  },

  inter: {
    enter: createWorkflow,
    steps: [
      null,
      (t) => dbSourceChain(t.nodes),
      (t) => [
        tool('add_node', { type: 'branch', label: t.nodes.branch, position: { x: 820, y: 120 }, config: { condition: 'amount > 1000000', defaultPath: 'FALSE' } }),
        connect(t.nodes.orderData, t.nodes.branch),
      ],
      (t) => [
        tool('add_node', { type: 'filter', label: t.nodes.filterHigh, position: { x: 1080, y: 40 }, config: { condition: 'amount > 1000000' } }),
        tool('add_node', { type: 'innerData', label: t.nodes.highOrders, position: { x: 1340, y: 40 }, config: {} }),
        connect(t.nodes.branch, t.nodes.filterHigh),
        connect(t.nodes.filterHigh, t.nodes.highOrders),
      ],
      (t) => [
        tool('add_node', { type: 'transformLoad', label: t.nodes.passAll, position: { x: 1080, y: 220 }, config: MOVE }),
        tool('add_node', { type: 'innerData', label: t.nodes.allOrders, position: { x: 1340, y: 220 }, config: {} }),
        connect(t.nodes.branch, t.nodes.passAll, 'false-path'),
        connect(t.nodes.passAll, t.nodes.allOrders),
      ],
      (t) => [
        tool('add_node', { type: 'merge', label: t.nodes.merge, position: { x: 1600, y: 120 }, config: { joinType: 'ANY' } }),
        tool('add_node', { type: 'transformLoad', label: t.nodes.load, position: { x: 1860, y: 120 }, config: MOVE }),
        tool('add_node', { type: 'rdbData', label: t.nodes.branchResult, position: { x: 2120, y: 120 }, config: { connection: PG, table: 'of_test_tgt_branch', mode: 'WRITE', writeMode: 'INSERT', columns: ORDER_COLS } }),
        connect(t.nodes.highOrders, t.nodes.merge),
        connect(t.nodes.allOrders, t.nodes.merge),
        connect(t.nodes.merge, t.nodes.load),
        connect(t.nodes.load, t.nodes.branchResult),
      ],
    ],
    verify: [tool('count_table_rows', { connection: PG, table: 'of_test_tgt_branch' })],
  },

  adv: {
    enter: createWorkflow,
    steps: [
      null,
      (t) => dbSourceChain(t.nodes),
      (t) => [
        tool('add_node', { type: 'quality', label: t.nodes.quality, position: { x: 820, y: 120 }, config: {
          onViolation: 'reject', maxViolationPct: '',
          rules: [
            { column: 'customer', rule: 'not_null', param: '', message: t.nodes.ruleNoCustomer },
            { column: 'status', rule: 'in_list', param: 'A,C', message: '' },
            { column: 'amount', rule: 'range', param: '0,500000', message: t.nodes.ruleAmountRange },
            { column: 'order_id', rule: 'unique', param: '', message: '' },
          ],
        } }),
        connect(t.nodes.orderData, t.nodes.quality),
      ],
      (t) => [
        tool('add_node', { type: 'innerData', label: t.nodes.passOrders, position: { x: 1080, y: 40 }, config: {} }),
        tool('add_node', { type: 'transformLoad', label: t.nodes.load, position: { x: 1340, y: 40 }, config: MOVE }),
        tool('add_node', { type: 'rdbData', label: t.nodes.targetOrders, position: { x: 1600, y: 40 }, config: { connection: PG, table: 'of_test_tgt_orders', mode: 'WRITE', writeMode: 'INSERT', columns: ORDER_COLS } }),
        connect(t.nodes.quality, t.nodes.passOrders),
        connect(t.nodes.passOrders, t.nodes.load),
        connect(t.nodes.load, t.nodes.targetOrders),
      ],
      (t) => [
        tool('add_node', { type: 'innerData', label: t.nodes.rejectOrders, position: { x: 1080, y: 220 }, config: {} }),
        tool('add_node', { type: 'transformLoad', label: t.nodes.rejectLog, position: { x: 1340, y: 220 }, config: MOVE }),
        tool('add_node', { type: 'csvData', label: t.nodes.rejectFile, position: { x: 1600, y: 220 }, config: { filepath: 'out/rejects.csv', delimiter: ',', mode: 'WRITE', hasHeader: true, encoding: 'UTF-8' } }),
        connect(t.nodes.quality, t.nodes.rejectOrders, 'reject-path'),
        connect(t.nodes.rejectOrders, t.nodes.rejectLog),
        connect(t.nodes.rejectLog, t.nodes.rejectFile),
      ],
    ],
    verify: [tool('count_table_rows', { connection: PG, table: 'of_test_tgt_orders' }), tool('count_file_rows', { filepath: 'out/rejects.csv' })],
  },
};

export interface StepPoint { target: string; label: string }

/**
 * 단계의 「대신 해줘」 호출에서 가리킬 곳을 끌어낸다 — 글(content)에 대상을 따로 적지 않아도 되게.
 * 노드를 넣는 단계면 툴박스의 그 항목, 잇기만 하는 단계면 잇기 시작할 노드.
 */
function pointOf(actions?: HelpAction[]): StepPoint | undefined {
  const add = actions?.find((a) => a.name === 'add_node');
  if (add) {
    const type = String((add.input as any).type || '');
    return { target: `toolbox:${type}`, label: String(i18n.t('assistant:pointer.drag', { name: typeLabelOf(type) })) };
  }
  const con = actions?.find((a) => a.name === 'connect_nodes');
  if (con) {
    const src = String((con.input as any).source || '');
    return { target: `node:${src}`, label: String(i18n.t('assistant:pointer.connect', { name: src })) };
  }
  return undefined;
}
const clickPoint = (target: string, nameKey: string): StepPoint =>
  ({ target, label: String(i18n.t('assistant:pointer.click', { name: i18n.t(nameKey) })) });

/** 글 + 동작 → 한 트랙 */
function buildTrack(id: QuickTrack['id'], c: HelpContent): QuickTrack {
  const text = c.tracks[id];
  const acts = ACTIONS[id];
  const L = c.labels;
  const total = text.steps.length + 5;          // 고유 단계 + 저장·배포·실행·확인·마무리
  const own: QuickStep[] = text.steps.map((s: StepText, i: number) => {
    const doActions = acts.steps[i]?.(text);
    return {
      title: n(i + 1, total, s.title),
      text: s.text,
      ...(i === 0 && acts.enter ? { action: acts.enter(text) } : {}),
      ...(doActions ? { doActions, doneText: s.doneText } : {}),
      ...(pointOf(doActions) ? { point: pointOf(doActions) } : {}),
      options: doActions ? [L.did, L.doForMe, L.stop] : [L.next, L.stop],
    };
  });
  const from = own.length + 1;
  const common: QuickStep[] = [
    { title: n(from, total, c.ui.save.title), text: c.ui.save.text, doActions: [tool('save_workflow')], doneText: c.ui.save.doneText, options: [L.did, L.doForMe, L.stop], point: clickPoint('toolbar:save', 'common:ui.save') },
    { title: n(from + 1, total, c.ui.deploy.title), text: c.ui.deploy.text, doActions: [tool('deploy_workflow')], doneText: c.ui.deploy.doneText, options: [L.did, L.doForMe, L.stop], point: clickPoint('toolbar:deploy', 'common:ui.deploy') },
    { title: n(from + 2, total, c.ui.run.title), text: c.ui.run.text, doActions: [tool('run_workflow')], doneText: c.ui.run.doneText, options: [L.did, L.doForMe, L.stop], point: clickPoint('toolbar:run', 'common:ui.run') },
    { title: n(from + 3, total, text.verify.title), text: text.verify.text, doActions: acts.verify, doneText: text.verify.doneText, options: [L.did, L.doForMe, L.stop],
      point: { target: 'toolbar:history', label: String(i18n.t('assistant:pointer.check')) } },
    { title: n(from + 4, total, c.ui.finishTitle), text: text.finish, options: [L.otherTrack, L.topics, L.free, L.done] },
  ];
  return { id, label: text.label, workflow: text.workflow, steps: [...own, ...common] };
}

/** 지금 언어의 트랙 셋 */
export function quickTracks(c: HelpContent = helpContent()): QuickTrack[] {
  return (['basic', 'inter', 'adv'] as const).map((id) => buildTrack(id, c));
}

export const trackById = (id?: string | null) => quickTracks().find((t) => t.id === id) || null;

/** 어느 언어의 버튼 글이든 알아본다 — 대화 중에 언어를 바꿀 수 있다 */
export const trackByLabel = (label: string, contents: HelpContent[]) => {
  const want = String(label || '').trim();
  for (const c of contents) {
    const id = (['basic', 'inter', 'adv'] as const).find((k) => c.tracks[k].label === want);
    if (id) return buildTrack(id, helpContent());
  }
  return null;
};

export default quickTracks;
